import { useCallback } from "react";
import { STORAGE_KEYS } from "../constants";
import { StravaDestination, CollectionListHandle, isValidCollectionListHandle } from "../types";

/**
 * Destination expiry time (15 minutes in milliseconds).
 *
 * Note: This is intentionally shorter than PREVIEW_DATA_EXPIRY_MS (6 hours).
 * - Destination (15min): Represents the user's immediate intent when starting OAuth.
 *   If they take longer than 15 minutes, their original context may no longer be relevant,
 *   so we fall back to the default collection list.
 * - Preview data (6hrs): Matches Strava access token lifetime for security. The activity
 *   data itself remains valid for hours and represents what the user wants to create.
 */
const DESTINATION_EXPIRY_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Result from getDestination including expiry status.
 */
export interface GetDestinationResult {
  /** The destination, or null if not found or expired */
  destination: StravaDestination | null;
  /** True if a destination was found but had expired */
  wasExpired: boolean;
}

/**
 * Hook for managing Strava flow destination in localStorage.
 *
 * This tracks where to redirect users after they complete the Strava OAuth flow.
 * Supports both collection pages and collection list pages.
 */
export const useStravaDestination = () => {
  /**
   * Save destination before starting Strava OAuth flow.
   *
   * @param collectionId - ID of specific collection, or null for collection list
   * @param collectionListHandle - Handle for collection list page (e.g., "A", "B", "featured")
   */
  const saveDestination = useCallback(
    (collectionId: string | null, collectionListHandle: CollectionListHandle): void => {
      const destination: StravaDestination = {
        collectionId,
        collectionListHandle,
        createdAt: Date.now(),
      };
      localStorage.setItem(STORAGE_KEYS.DESTINATION, JSON.stringify(destination));
    },
    []
  );

  /**
   * Get destination from localStorage.
   * Returns destination and expiry status.
   * Returns null if not found, expired, or invalid.
   */
  const getDestination = useCallback((): GetDestinationResult => {
    const stored = localStorage.getItem(STORAGE_KEYS.DESTINATION);
    if (!stored) {
      return { destination: null, wasExpired: false };
    }

    try {
      const destination: StravaDestination = JSON.parse(stored);

      // Check if destination has expired
      if (Date.now() - destination.createdAt > DESTINATION_EXPIRY_MS) {
        console.log("Strava destination has expired, clearing");
        localStorage.removeItem(STORAGE_KEYS.DESTINATION);
        return { destination: null, wasExpired: true };
      }

      // Validate the collectionListHandle to ensure it's a known value
      if (!isValidCollectionListHandle(destination.collectionListHandle)) {
        console.warn(
          `Invalid collectionListHandle "${destination.collectionListHandle}", clearing destination`
        );
        localStorage.removeItem(STORAGE_KEYS.DESTINATION);
        return { destination: null, wasExpired: false };
      }

      return { destination, wasExpired: false };
    } catch (error) {
      console.error("Failed to parse Strava destination from localStorage:", error);
      localStorage.removeItem(STORAGE_KEYS.DESTINATION);
      return { destination: null, wasExpired: false };
    }
  }, []);

  /**
   * Clear destination from localStorage.
   * Call this after successfully navigating to the destination.
   */
  const clearDestination = useCallback((): void => {
    localStorage.removeItem(STORAGE_KEYS.DESTINATION);
  }, []);

  return {
    saveDestination,
    getDestination,
    clearDestination,
  };
};
